import { generateClient } from "aws-amplify/api";
import { FormGroup, FormGroupForResolver, FormInstance, FormInstanceForResolver } from "db/definitions";
import { createFormGroup } from "../../graphql/mutations";
import { db } from "../db";
import { bulkRetrieveFieldInstancesByFormGroup, deleteFieldInstancesByFormGroupIndex } from "./fieldInstanceQueries";
import { buildFormInstancesByFormGroupId, deleteFormInstanceByFormGroupId, getFormInstancesByFormGroup } from "./formInstanceQueries";
import { bulkRetrieveFormTemplates, getFullFormTemplateOffline } from "./formTemplateQueries";
import { bulkDeleteImageBinaryDocs } from "./imageBinaryQueries";
import { bulkDeleteImageDocs, bulkGetImageDocs } from "./imageQueries";
import { bulkDeleteRevisionsOffline, getRevisionsByFieldInstance } from "./revisionQueries";

/**
 * Given the ids, gets all photologs and builds them out enough to be used in the batchUploadFromPhotolog area
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroupIds The ids of the formGroups that are photologs
 * @returns The built out enough photologs
 */
export async function batchGetOfflinePhotologs(formGroupIds: string[]) {
	const formGroupsPromise = bulkGetFormGroups(formGroupIds);
	const formInstancesPromises: Promise<FormInstanceForResolver[]>[] = [];
	for (const id of formGroupIds) {
		formInstancesPromises.push(getFormInstancesByFormGroup(id));
	}
	const [formGroups, formInstancesByFormGroup] = await Promise.all([formGroupsPromise, await Promise.all(formInstancesPromises)]);
	const finalFormGroupPromises = [];
	for (let i = 0; i < formInstancesByFormGroup.length; i++) {
		finalFormGroupPromises.push(buildFinalFormGroup(i));
	}
	const finalFormGroups = await Promise.all(finalFormGroupPromises);

	return finalFormGroups;

	async function buildFinalFormGroup(i: number) {
		const formGroup = formGroups[i];
		const buildFormInstancePromises = [];
		for (const formInstance of formInstancesByFormGroup[i]) {
			buildFormInstancePromises.push(grabFormTemplateAndBuildInstance(formInstance));
		}
		const formInstances = (await Promise.all(buildFormInstancePromises)).filter((formInstance) => {
			return formInstance !== undefined;
		});
		const finalFormGroup: FormGroup = { ...formGroup, formInstances: { items: formInstances } };
		return finalFormGroup;
	}

	async function grabFormTemplateAndBuildInstance(formInstance: FormInstanceForResolver): Promise<FormInstance | void> {
		const fullTemplate = await getFullFormTemplateOffline(formInstance.formTemplateId);
		if (fullTemplate) {
			return { ...formInstance, formTemplate: fullTemplate, fieldInstances: { items: [] } };
		}
	}
}

/**
 * Given a formGroup, retrieves all formInstances by its id, attaches them, and returns the final formGroup
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroup The formGroup we need to build out
 * @returns The formGroup, built down to the formInstance level
 */
export async function buildFormGroupDownToFormInstances(formGroup: FormGroupForResolver): Promise<FormGroup> {
	// We need to construct the formGroup with all formInstances
	// Step 1: Get all formInstances where its "formGroup" is the formGroup's id
	// Step 2: Attach the formInstances to the formGroup and return it

	// Step 1: Get the formInstances by formGroupId. I need the "as FormInstance[]" to then assign the formTemplate to each one
	const formInstances = (await getFormInstancesByFormGroup(formGroup.id)) as FormInstance[];
	const formTemplates = await bulkRetrieveFormTemplates(formInstances.map((fi) => fi.formTemplateId));
	for (let i = 0; i < formInstances.length; i++) {
		formInstances[i].formTemplate = formTemplates[i];
	}

	// Step 2: Attach the formInstances to the formGroup and return it
	const formGroupToReturn: FormGroup = { ...formGroup, formInstances: { items: formInstances } };
	return formGroupToReturn;
}

/**
 * Given the formGroup retrieved offline, goes through and attaches all of the formInstances to it and returns
 * USE THIS IN THE _getProject UTIL, NOT useForms HOOK! THEY NEED DIFFERENT STUFF IN THE formGroup!!!!
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroup FormGroup object, straight from offline db
 * @returns A completely formGroup with all formInstances
 */
export async function buildFormGroupForFormGroupList(formGroup: FormGroupForResolver) {
	// We need to construct the formGroup with all formInstances
	// Step 1: Get all formInstances where its "formGroup" is the formGroup's id
	// Step 2: Get array of formTemplate ids from formInstances
	// Step 3: BulkGet formTemplates with ids
	// Step 4: Assemble formInstances
	// Step 5: Attach the formInstances to the formGroup and return it

	// Step 1: Get the formInstances by formGroupId
	const formInstances = await getFormInstancesByFormGroup(formGroup.id);
	// Step 2: Get array of formTemplateIds from formInstances
	const formTempalteIds = formInstances.map((fi) => fi.formTemplateId);
	// Step 3: BulkGet formTemplates with ids
	const formTemplates = await bulkRetrieveFormTemplates(formTempalteIds);
	// Step 4: Assemble formInstances
	const finalFormInstances = formInstances.map((fi, index) => {
		const copy = { ...fi, fieldInstances: { items: [] }, formTemplate: formTemplates[index] };
		return copy;
	});
	// Step 5: Attach the formInstances to the formGroup and return it
	const formGroupToReturn = { ...formGroup, formInstances: { items: finalFormInstances } };
	return formGroupToReturn;
}

/**
 * Given an array of formGroup ids, retrieves all of them and returns it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids The formGroup ids
 * @returns All retrieved formGroups
 */
export async function bulkGetFormGroups(ids: string[]) {
	const retrievedFormGroups = await db.formGroup.bulkGet(ids);
	const formGroups = retrievedFormGroups.filter((fg): fg is FormGroupForResolver => fg !== undefined);
	return formGroups;
}

/**
 * Given the ids of the photologs, retrieves them, builds them, and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids The ids fo the photologs (formGroups) we need to get
 */
export async function bulkGetPhotoLogs(ids: string[]) {
	const formGroups = await bulkGetFormGroups(ids);
	const formInstanceArrayPromises = [];
	for (const id of ids) {
		formInstanceArrayPromises.push(buildFormInstancesByFormGroupId(id));
	}
	const formInstanceArrays = await Promise.all(formInstanceArrayPromises);
	const finalFormGroups = formGroups.map((fg, index) => {
		const fgCopy: FormGroup = { ...fg };
		fgCopy.formInstances = { items: [...formInstanceArrays[index]] };
		return fgCopy;
	});
	return finalFormGroups;
}

/**
 * Given an array of formGroup documents, saves them to the indexeddb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param {Object[]} documents The formGroup documents we want to save
 */
export async function bulkSaveFormGroupDocs(documents: FormGroupForResolver[]) {
	await db.formGroup.bulkPut(documents);
}

/**
 * Given the formGroup id, queries the indexedDb to see if the formGroup exists, returning it, its formInstances, and its fieldInstances
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the formGroup we want to check if exists
 * @returns The formGroup, its formInstances, and its fieldInstances OR false if it doesn't exist
 */
export async function checkIfFormGroupExistsAndGrab(id: string) {
	// Very simple function, gets all documents within a formGroup, maps them, and returns the result (or false if there's nothing)
	// Step 1: Query the offline formGroup. If it returns falsy, return false
	// Step 2: Get all formInstances/fieldInstances within the formGroup
	// Step 3: Convert the returned arrays into maps for easy access
	// Step 4: If the fieldInstance docs have imageIds, throw those into an array to bulk grab
	// Step 5: BulkGet the image docs
	// Step 6: Map the image docs
	// Step 7: Return everything

	// Step 1: Query the offline formGroup. If it returns falsy, return false
	const result = await getOfflineFormGroupById(id);
	if (result) {
		// Step 2: Get all formInstances/fieldInstances within the formGroup
		const [formInstances, fieldInstances] = await Promise.all([getFormInstancesByFormGroup(id), bulkRetrieveFieldInstancesByFormGroup(id)]);

		// Step 3: Convert the returned arrays into maps for easy access
		const formInstanceMap = new Map();
		const fieldInstanceMap = new Map();
		const imageMap = new Map();
		for (const formInstance of formInstances) {
			formInstanceMap.set(formInstance.id, formInstance);
		}
		const imageIdsForBulkGet = [];
		for (const fieldInstance of fieldInstances) {
			fieldInstanceMap.set(fieldInstance.id, fieldInstance);
			// Step 4: If the fieldInstance docs have imageIds, throw those into an array to bulk grab
			if (fieldInstance.imageId) {
				imageIdsForBulkGet.push(fieldInstance.imageId);
			}
		}

		// Step 5: BulkGet the image docs
		const imageDocs = await bulkGetImageDocs(imageIdsForBulkGet);

		// Step 6: Map the image docs
		for (const image of imageDocs) {
			imageMap.set(image.id, image);
		}

		// Step 7: Return everything
		return { formGroup: result, formInstanceMap, fieldInstanceMap, imageMap };
	} else {
		return false;
	}
}

/**
 * Given a formGroup, trims off all properties that aren't doc specific, and returns it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param {Object} formGroup The formGroup
 * @returns The formGroup with all of the necessary properties
 */
export function createFormGroupForBatchResolver(formGroup: FormGroup | FormGroupForResolver) {
	const formGroupForBulkPut = {
		id: formGroup.id,
		users: formGroup.users,
		name: formGroup.name,
		projectId: formGroup.projectId,
		type: formGroup.type,
		formOrder: formGroup.formOrder,
		group: formGroup.group,
		createdAt: formGroup.createdAt,
		updatedAt: formGroup.updatedAt,
		lastUpdatedBy: formGroup.lastUpdatedBy,
		syncedAt: formGroup.syncedAt,
	};
	return formGroupForBulkPut;
}

/**
 * Given a formGroup object, inserts it into the correct database depending on whether or not the user is online
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroup The formGroup we want to put in the db
 * @param online Whether or not we're online
 */
export async function createFormGroupQuery(formGroup: FormGroupForResolver, online: boolean) {
	if (online) {
		const client = generateClient({ authMode: "userPool" });
		await client.graphql({ query: createFormGroup, authMode: "userPool", variables: { input: formGroup } });
	} else {
		await saveFormGroup(formGroup);
	}
}

/**
 * Given 2 formGroups, merges them together based off of their updatedAt property
 * @author Eric Webb <ewebb@factorearth.com>
 * @param leftFormGroup The "donor" formGroup
 * @param rightFormGroup The "receiver" formGroup
 * @returns The merged formGroup
 */
export function createMergedFormGroup(leftFormGroup: FormGroupForResolver, rightFormGroup: FormGroupForResolver): FormGroupForResolver {
	const timeStamp = new Date();
	const leftTimeStamp = leftFormGroup.updatedAt ? new Date(leftFormGroup.updatedAt) : timeStamp;
	const rightTimeStamp = rightFormGroup.updatedAt ? new Date(rightFormGroup.updatedAt) : timeStamp;
	const usersToUse = getFormGroupUsers(leftFormGroup, rightFormGroup);
	const formGroupForBulkPut: FormGroupForResolver = {
		id: rightFormGroup.id,
		users: usersToUse,
		name: leftTimeStamp > rightTimeStamp ? leftFormGroup.name : rightFormGroup.name,
		projectId: rightFormGroup.projectId,
		type: rightFormGroup.type,
		formOrder: [...new Set([...leftFormGroup.formOrder, ...rightFormGroup.formOrder])],
		group: rightFormGroup.group,
		createdAt: rightFormGroup.createdAt || leftFormGroup.createdAt || timeStamp.toISOString(),
		updatedAt: leftTimeStamp > rightTimeStamp ? leftTimeStamp.toISOString() : rightTimeStamp.toISOString(),
		lastUpdatedBy: rightFormGroup.lastUpdatedBy || leftFormGroup.lastUpdatedBy,
		syncedAt: rightFormGroup.syncedAt || leftFormGroup.syncedAt || timeStamp.toISOString(),
	};
	return formGroupForBulkPut;
}

/**
 * Given a formGroup, deletes all formInstances by the formGroup id, as well as the formGroup itself
 * OFFLINE
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroup The formGroup we're trying to delete
 */
export async function deleteFormGroup(formGroup: Pick<FormGroup, "id"> & Partial<FormGroup | FormGroupForResolver>) {
	const fields = await bulkRetrieveFieldInstancesByFormGroup(formGroup.id);
	const imageIdsToDelete = [];
	const revisionsToDelete = [];
	const revisionPromises = [];
	for (const field of fields) {
		if (field.imageId) imageIdsToDelete.push(field.imageId);
		const thisFieldsRevisions = getRevisionsByFieldInstance(field.id, false);
		revisionPromises.push(thisFieldsRevisions);
	}
	const resolvedRevisions = await Promise.all(revisionPromises);
	for (let i = 0; i < resolvedRevisions.length; i++) {
		const oneFieldsRevisions = resolvedRevisions[i];

		for (let ii = 0; ii < oneFieldsRevisions.length; ii++) {
			const oneRevision = oneFieldsRevisions[ii];
			revisionsToDelete.push(oneRevision.id);
		}
	}

	const imageDocs = await bulkGetImageDocs(imageIdsToDelete);
	const imageBinaryIdsToDelete = [];
	for (const image of imageDocs) {
		if (image.key && typeof image.key === "string") imageBinaryIdsToDelete.push(image.key);
	}
	await Promise.all([
		deleteFormGroupById(formGroup.id),
		deleteFormInstanceByFormGroupId(formGroup.id),
		deleteFieldInstancesByFormGroupIndex(formGroup.id),
		bulkDeleteImageDocs(imageIdsToDelete),
		bulkDeleteImageBinaryDocs(imageBinaryIdsToDelete),
		bulkDeleteRevisionsOffline(revisionsToDelete),
	]);
}

/**
 * Given a formGroup's id, deletes it from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the formGroup
 */
async function deleteFormGroupById(id: string) {
	await db.formGroup.delete(id);
}

/**
 * Given the formGroup id, queries the indexedDb and returns the document
 * @author Eric Webb <ewebb@factorearth.com>
 * @param {string} id The formGroup's id
 * @returns The formGroup doc
 */
export async function getFormGroupById(id: string): Promise<FormGroupForResolver | undefined> {
	const formGroup = await db.formGroup.get(id);
	return formGroup;
}

/**
 * Given a project id, returns all formGroups within the project
 * @author Eric Webb <ewebb@factorearth.com>
 * @param {string} id The id of the project
 * @returns All formGroups within a project
 */
export async function getFormGroupsByProjectId(id: string) {
	const formGroups = await db.formGroup.where("projectId").equals(id).toArray();
	return formGroups;
}

/**
 * Given the left and right formGroups, takes their user properties and returns the value to use to merge them
 * @auhtor Eric Webb <ewebb@factorearth.com>
 * @param leftFormGroup The "donor" formGroup
 * @param rightFormGroup The "receiver" formGroup
 * @returns The value that needs to be used for merging the formGroup
 */
function getFormGroupUsers(leftFormGroup: FormGroupForResolver, rightFormGroup: FormGroupForResolver): string[] {
	// The property is null by default in aws console, so that's why this isn't an empty array
	if (!leftFormGroup.users && !rightFormGroup.users) return [];
	// Left has some users (so can't be null), right doesn't, so use left
	if (leftFormGroup.users && !rightFormGroup.users) return leftFormGroup.users;
	// Right has some users (so can't be null), left doesn't, so use right
	if (rightFormGroup.users && !leftFormGroup.users) return rightFormGroup.users;
	if (leftFormGroup.users && rightFormGroup.users) {
		// Both somehow have empty arrays
		if (leftFormGroup.users.length === 0 && rightFormGroup.users?.length === 0) return [];
		// Combine them
		return [...new Set([...rightFormGroup.users, ...leftFormGroup.users])];
	}
	return [];
}

/**
 * Given the id of the formGroup, retrieves it and returns it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the formGroup that we want to retrieve
 * @returns The formGroup document
 */
export async function getOfflineFormGroupById(id: string) {
	const formGroup = await db.formGroup.get(id);
	return formGroup;
}

/**
 * Queries the count of all FormGroups, multiplies the count by the average size, and returns that value
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The filesize, in bytes, of all formGroups
 */
export async function getTotalFormGroupDiskSize() {
	const number = await db.formGroup.count();
	const AVERAGE_DOCUMENT_SIZE = 297.74;
	return number * AVERAGE_DOCUMENT_SIZE;
}

export async function saveFormGroup(formGroup: FormGroupForResolver) {
	await db.formGroup.put(formGroup);
}

/**
 * Given an object with kvps for properties on the formGroup, as well as the id, updates the formGroup in the indexedDb with the values
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroup The formGroup data we want to update
 * @returns The success status of the operation
 */
export async function updateFormGroup(formGroup: FormGroupForResolver): Promise<number> {
	formGroup.updatedAt = new Date().toISOString();
	const status = await db.formGroup.update(formGroup.id, formGroup as any);
	if (!status) {
		console.error("FORM GROUP NOT FOUND ", formGroup);
	}
	return status;
}

/**
 * Given an object with kvps for properties on the formGroup, as well as the id, updates the formGroup in the indexedDb with the values
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroup The formGroup data we want to update
 * @returns The success status of the operation
 */
export async function updateFormGroupNameOffline(data: { id: string; name: string; group: string; updatedAt: string }): Promise<number> {
	// const newOne = {
	// 	name: data.name,
	// 	updatedAt: data.updatedAt
	// };
	const status = await db.formGroup.update(data.id, data as any);
	if (!status) {
		console.error("FORM GROUP NOT FOUND ", data);
	}
	return status;
}
